import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  findMahjong,
  inviteMahjong,
  startMahjong,
  stopFindingMahjong,
  type GameAgentPick,
  type MahjongSettings,
} from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open } from '../chatv1/ws'
import { Icon } from '../ui/Icon'
import { Avatar } from '../chat/Avatar'
import { getAttachableAgents, getFriends, type AttachableAgent } from '../transport/api'
import { gameErrorText } from './gameText'

/**
 * ＋ → GAME → MAHJONG — owner, 2026-10-26: 開始做麻將; 邀請者可以預設籌碼. Four seats: invite up to three friends, sit
 * down with whoever is looking, or play with your own agents — the computer takes any seat left. Whoever sets the
 * table up chooses the chips each seat starts with, the stakes (the base, and the chips each 台 is worth) and how
 * long it lasts.
 */

type View = 'menu' | 'friends' | 'agents' | 'finding'

const CHIPS = [500, 1000, 2000, 5000, 10000] as const
const STAKES: ReadonlyArray<[number, number]> = [
  [10, 5],
  [30, 10],
  [50, 20],
  [100, 20],
  [100, 50],
  [300, 100],
]
const DEFAULT_SETTINGS: MahjongSettings = { chips: 1000, base: 30, per_tai: 10, rounds: 1 }

export function MahjongLobby({ onOpen, onBack }: { onOpen: (id: string) => void; onBack: () => void }) {
  const [view, setView] = useState<View>('menu')
  const back = (to: View | 'games', label: string) => (
    <button type="button" className="ac-back" aria-label="Back" onClick={() => (to === 'games' ? onBack() : setView(to))}>
      <Icon name="chevron_left" size={18} />
      {label}
    </button>
  )
  if (view === 'friends') {
    return (
      <>
        {back('menu', 'Mahjong')}
        <InviteFriends onInvited={onOpen} />
      </>
    )
  }
  if (view === 'agents') {
    return (
      <>
        {back('menu', 'Mahjong')}
        <PlayAgents onStarted={onOpen} />
      </>
    )
  }
  if (view === 'finding') {
    return (
      <>
        {back('menu', 'Mahjong')}
        <FindTable onMatched={onOpen} onAgentsInstead={() => setView('agents')} onCancel={() => setView('menu')} />
      </>
    )
  }
  return (
    <>
      {back('games', 'Games')}
      <div className="gm-lobby">
        <button type="button" className="gm-big" onClick={() => setView('friends')}>
          <Icon name="group" size={22} />
          <span>
            <strong>Invite friends</strong>
            <span className="gm-big-sub">Up to three friends, with your chips and stakes — the computer fills any seat left.</span>
          </span>
        </button>
        <button type="button" className="gm-big" onClick={() => setView('finding')}>
          <Icon name="groups" size={22} />
          <span>
            <strong>Find players</strong>
            <span className="gm-big-sub">Sit down with whoever is looking for a game; the computer fills the rest.</span>
          </span>
        </button>
        <button type="button" className="gm-big" onClick={() => setView('agents')}>
          <Icon name="smart_toy" size={22} />
          <span>
            <strong>Play with your agents</strong>
            <span className="gm-big-sub">Up to three of your agents take seats; the computer fills the rest.</span>
          </span>
        </button>
      </div>
    </>
  )
}

/** The inviter's chips (邀請者可以預設籌碼): what each seat starts with, the stakes, and how long. */
function TableSettings({ value, onChange }: { value: MahjongSettings; onChange: (v: MahjongSettings) => void }) {
  return (
    <>
      <div className="nas-sec">Chips for each seat</div>
      <div className="vm-seg mj-seg" role="radiogroup" aria-label="Chips for each seat">
        {CHIPS.map((c) => (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={value.chips === c}
            className={`vm-seg-btn${value.chips === c ? ' on' : ''}`}
            onClick={() => onChange({ ...value, chips: c })}
          >
            {c.toLocaleString('en-US')}
          </button>
        ))}
      </div>
      <div className="nas-sec">Stakes — base / each 台</div>
      <div className="vm-seg mj-seg" role="radiogroup" aria-label="Stakes">
        {STAKES.map(([b, p]) => {
          const on = value.base === b && value.per_tai === p
          return (
            <button
              key={`${b}/${p}`}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={`Base ${b}, ${p} for each 台`}
              className={`vm-seg-btn${on ? ' on' : ''}`}
              onClick={() => onChange({ ...value, base: b, per_tai: p })}
            >
              {b}/{p}
            </button>
          )
        })}
      </div>
      <div className="nas-sec">Length</div>
      <div className="vm-seg" role="radiogroup" aria-label="Length">
        {(
          [
            [1, 'East round'],
            [4, 'Four rounds'],
          ] as const
        ).map(([r, label]) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={value.rounds === r}
            className={`vm-seg-btn${value.rounds === r ? ' on' : ''}`}
            onClick={() => onChange({ ...value, rounds: r })}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="gh-note">
        A win takes the base plus {value.per_tai} for each 台 — from whoever discarded the tile, or from all three when it was
        self-drawn. The East round is four deals or more (the dealer keeps the deal by winning it); a seat that runs out of
        chips ends the game.
      </p>
    </>
  )
}

/** Up to `max` of your agents, each in its own seat. */
function AgentsPick({ picked, onPick, max }: { picked: GameAgentPick[]; onPick: (a: GameAgentPick[]) => void; max: number }) {
  const agents = useQuery({ queryKey: ['attachable-agents'], queryFn: () => getAttachableAgents() })
  const rows = (agents.data?.agents ?? []).filter((a): a is AttachableAgent & { device_id: string } => !!a.device_id)
  if (agents.isPending) return <p className="gh-note">Loading your agents…</p>
  if (agents.isError) return <p className="gh-note mch-err">Your agents could not be loaded — try again.</p>
  if (!rows.length) return <p className="gh-note">No agents yet — the computer takes every seat. Start one with ＋ → Agent.</p>
  return (
    <div className="ac-menu gm-agents" role="group" aria-label="Your agents">
      {rows.map((a) => {
        const on = picked.some((p) => p.agent_hash === a.channel_hash && p.device_id === a.device_id)
        const full = !on && picked.length >= max
        return (
          <button
            key={`${a.device_id}/${a.channel_hash}`}
            type="button"
            role="checkbox"
            aria-checked={on}
            disabled={full}
            className={`ac-row${on ? ' on' : ''}`}
            onClick={() =>
              onPick(
                on
                  ? picked.filter((p) => !(p.agent_hash === a.channel_hash && p.device_id === a.device_id))
                  : [...picked, { agent_hash: a.channel_hash, device_id: a.device_id, name: a.label }],
              )
            }
          >
            <Icon name="smart_toy" size={18} />
            <span className="ac-row-label">{a.label}</span>
            {a.device_name && <span className="ac-row-val">{a.device_name}</span>}
            {on && <Icon name="check" size={18} className="ac-row-go" />}
          </button>
        )
      })}
    </div>
  )
}

function PlayAgents({ onStarted }: { onStarted: (id: string) => void }) {
  const [agents, setAgents] = useState<GameAgentPick[]>([])
  const [settings, setSettings] = useState<MahjongSettings>(DEFAULT_SETTINGS)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const start = async () => {
    setBusy(true)
    setErr(null)
    try {
      onStarted((await startMahjong(agents, settings)).mahjong.id)
    } catch (e) {
      setErr(gameErrorText(e))
    } finally {
      setBusy(false)
    }
  }
  const computers = 3 - agents.length
  return (
    <div className="gm-form">
      <div className="nas-sec">Your agents (up to three)</div>
      <AgentsPick picked={agents} onPick={setAgents} max={3} />
      <p className="gh-note">
        {agents.length
          ? `Each agent is sent its hand on its turn and answers with its play — it may take a little while.${computers ? ` The computer plays the other ${computers === 1 ? 'seat' : `${computers} seats`}.` : ''}`
          : 'Pick none and you play three computer players.'}
      </p>
      <TableSettings value={settings} onChange={setSettings} />
      {err && <p className="gh-note mch-err">{err}</p>}
      <div className="svr-createrow svr-creatego">
        <button type="button" className="sheet-btn primary" disabled={busy} onClick={() => void start()}>
          Start game
        </button>
      </div>
    </div>
  )
}

function InviteFriends({ onInvited }: { onInvited: (id: string) => void }) {
  const friends = useQuery({ queryKey: ['friends'], queryFn: getFriends })
  const [emails, setEmails] = useState<string[]>([])
  const [agents, setAgents] = useState<GameAgentPick[]>([])
  const [settings, setSettings] = useState<MahjongSettings>(DEFAULT_SETTINGS)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const rows = friends.data?.friends ?? []
  const send = async () => {
    setBusy(true)
    setErr(null)
    try {
      onInvited((await inviteMahjong(emails, agents, settings)).mahjong.id)
    } catch (e) {
      setErr(gameErrorText(e))
    } finally {
      setBusy(false)
    }
  }
  const room = 3 - emails.length
  return (
    <div className="gm-form">
      <div className="nas-sec">Your friends (up to three)</div>
      {friends.isPending && <p className="gh-note">Loading your friends…</p>}
      {friends.isError && <p className="gh-note mch-err">Your friends could not be loaded — try again.</p>}
      {friends.isSuccess && !rows.length && <p className="gh-note">No friends yet. Add one in Humans, then come back.</p>}
      {rows.length > 0 && (
        <div className="ac-menu gm-agents" role="group" aria-label="Your friends">
          {rows.map((f) => {
            const on = emails.includes(f.email)
            const full = !on && emails.length + agents.length >= 3
            return (
              <button
                key={f.email}
                type="button"
                role="checkbox"
                aria-checked={on}
                disabled={full}
                className={`ac-row${on ? ' on' : ''}`}
                onClick={() => setEmails(on ? emails.filter((e) => e !== f.email) : [...emails, f.email])}
              >
                <Avatar picture={f.picture} label={f.name || f.email} email={f.email} className="gm-face gm-face-sm" />
                <span className="ac-row-label">{f.name || f.email}</span>
                {on && <Icon name="check" size={18} className="ac-row-go" />}
              </button>
            )
          })}
        </div>
      )}
      {emails.length > 0 && room > 0 && (
        <>
          <div className="nas-sec">Your agents for the other {room === 1 ? 'seat' : 'seats'} (optional)</div>
          <AgentsPick picked={agents} onPick={setAgents} max={room} />
        </>
      )}
      <TableSettings value={settings} onChange={setSettings} />
      {err && <p className="gh-note mch-err">{err}</p>}
      <div className="svr-createrow svr-creatego">
        <button type="button" className="sheet-btn primary" disabled={!emails.length || busy} onClick={() => void send()}>
          Send {emails.length > 1 ? 'invitations' : 'invitation'}
        </button>
      </div>
    </div>
  )
}

/** How long a wait stays up — the service's own ticket backstop. Nothing re-asks meanwhile. */
const WAIT_MS = 600_000

/** Looking for players: asked once (again only when the socket returns); seated the moment someone else looks. */
function FindTable({ onMatched, onAgentsInstead, onCancel }: { onMatched: (id: string) => void; onAgentsInstead: () => void; onCancel: () => void }) {
  const [err, setErr] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const done = useRef(false)
  const onMatchedRef = useRef(onMatched)
  onMatchedRef.current = onMatched
  useEffect(() => {
    let alive = true
    const matched = (id: string) => {
      if (done.current) return
      done.current = true
      onMatchedRef.current(id)
    }
    const ask = async () => {
      try {
        const r = await findMahjong()
        if (alive && r.status === 'matched') matched(r.mahjong.id)
      } catch (e) {
        if (alive) setErr(gameErrorText(e))
      }
    }
    void ask()
    const offOpen = onChatv1Open(() => void ask())
    // A DEADLINE, not a poll: when the service's ticket would lapse, say so instead of spinning on.
    const deadline = setTimeout(() => {
      if (alive && !done.current) {
        setExpired(true)
        void stopFindingMahjong().catch(() => {})
      }
    }, WAIT_MS)
    const off = onChatv1Frame((f) => {
      if (f.type === 'mahjong' && f.game.mode === 'match' && f.game.status === 'active' && f.game.history.length === 0) matched(f.game.id)
    })
    return () => {
      alive = false
      clearTimeout(deadline)
      offOpen()
      off()
      if (!done.current) void stopFindingMahjong().catch(() => {})
    }
  }, [])
  return (
    <div className="gm-finding">
      {expired ? (
        <p className="gm-finding-say" role="status">
          Nobody else was looking just now.
        </p>
      ) : (
        <>
          <div className="gm-spinner" aria-hidden="true" />
          <p className="gm-finding-say" role="status">
            Looking for players…
          </p>
          <p className="gh-note">
            You sit down with whoever looks for a game while this stays open — the computer fills the other seats. Each of you
            sees the others’ names and pictures. Chips: 1,000 each, stakes 30/10, the East round.
          </p>
        </>
      )}
      {err && <p className="gh-note mch-err">{err}</p>}
      <div className="gm-row">
        <button type="button" className="fr-btn" onClick={onCancel}>
          Stop looking
        </button>
        <button type="button" className="fr-btn ok" onClick={onAgentsInstead}>
          Play with your agents instead
        </button>
      </div>
    </div>
  )
}
